import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import PasswordInputWithEye from "@/components/PasswordInputWithEye";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { login, completeAuth } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.token) {
        // OTP disabled: direct login
        completeAuth(result.token, result.user);
        toast.success("Bienvenue !");
        navigate("/app/dashboard");
      } else {
        // OTP flow: redirect to verify page
        navigate("/auth/verify", { state: { email: result.email, purpose: result.purpose } });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Connexion échouée");
    } finally {
      setLoading(false);
    }
  };

  // Show toast if redirected here because of session invalidation on another device
  useEffect(() => {
    const msg = sessionStorage.getItem("afrimeet_session_invalidated");
    if (msg) {
      sessionStorage.removeItem("afrimeet_session_invalidated");
      toast.warning(msg);
    }
  }, []);

  return (
    <div className="min-h-screen bg-af grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-12 bg-af-hero border-r border-af">
        <Link to="/" className="flex items-center gap-3" data-testid="auth-logo">
          <Logo size={52} />
          <span className="font-display font-extrabold text-2xl">AfriMeet</span>
        </Link>
        <div>
          <h2 className="font-display font-extrabold text-4xl leading-tight mb-4">
            Vos réunions, transformées par l'IA.
          </h2>
          <p className="text-af-secondary leading-relaxed max-w-md">
            Décisions, actions et risques structurés automatiquement après chaque réunion.
          </p>
        </div>
        <div className="text-xs text-af-muted">© 2026 AfriMeet</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8">
            <Link to="/" className="flex items-center gap-3">
              <Logo size={52} />
              <span className="font-display font-extrabold text-2xl">AfriMeet</span>
            </Link>
          </div>
          <h1 className="font-display font-extrabold text-3xl mb-2">Connexion</h1>
          <p className="text-af-secondary mb-8">Accédez à votre espace AfriMeet</p>
          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" required
                data-testid="login-email-input"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 border-af focus-visible:ring-terracotta"
                placeholder="vous@entreprise.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Mot de passe</Label>
                <Link
                  to="/auth/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-xs text-terracotta font-semibold hover:underline"
                >
                  Mot de passe oublié ?
                </Link>
              </div>
              <PasswordInputWithEye
                id="password"
                required
                dataTestId="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
            <Button
              type="submit" disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-terracotta hover:bg-[#9C7124] text-white h-11 rounded-md"
            >
              {loading ? "Connexion…" : "Se connecter"} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>
          <div className="mt-6 text-sm text-af-secondary text-center">
            Pas encore de compte ?{" "}
            <Link to="/auth/signup" className="text-terracotta font-semibold hover:underline" data-testid="goto-signup-link">
              Créer un compte
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
